import { ReactNode, forwardRef } from 'react';

import {
  Theme,
  WithStyles,
  alpha,
  createStyles,
  withStyles,
} from '@material-ui/core';

const getPlatform = (): string =>
  navigator.userAgentData?.platform || navigator?.platform || 'unknown';

const isMac = () => {
  return /MAC/.test(getPlatform().toUpperCase());
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      border: '1px solid',
      borderRadius: theme.shape.borderRadius,
      color: theme.palette.navigation.color,
      borderColor: alpha(theme.palette.navigation.color, 0.2),
      whiteSpace: 'nowrap',
      padding: theme.spacing(0, 1),
      fontSize: '0.875rem',
      minWidth: 20,
      minHeight: 20,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  });

interface KeyboardShortcutProps extends WithStyles<typeof styles> {
  hotkey?: string;
  children?: ReactNode;
}

const KeyboardShortcutImpl = forwardRef<HTMLSpanElement, KeyboardShortcutProps>(
  ({ hotkey, children, classes }, ref) => {
    const data = typeof children === 'string' ? children : hotkey;

    const translateHotkey = (key: string | undefined): string => {
      if (!key) return '';

      const parts = key.toLowerCase().split('+');

      const translatedParts = parts.map(part => {
        switch (part.trim()) {
          case 'meta':
            return isMac() ? '⌘' : 'Ctrl';
          case 'alt':
            return isMac() ? '⌥' : 'Alt';
          case 'shift':
            return isMac() ? '⇧' : 'Shift';
          case 'ctrl':
            return isMac() ? '⌃' : 'Ctrl';
          case 'v':
            // In switch case to prevent capitalization
            return 'v';
          case 'enter':
          case 'return':
            return isMac() ? '⏎' : 'Enter';
          default:
            // Capitalize single letters
            return part.length === 1
              ? part.toUpperCase()
              : part.charAt(0).toUpperCase() + part.slice(1);
        }
      });

      return translatedParts.join(' ');
    };

    return (
      <span ref={ref} className={classes.root}>
        {translateHotkey(data)}
      </span>
    );
  },
);

export const KeyboardShortcut = withStyles(styles)(KeyboardShortcutImpl);
